import {Config} from 'olympe';
import {tagToDisplayER} from './tagTransforms';

/**
 * Check if email addresses are in valid format, set them as lower case and remove duplicates
 * @param {string[]} emailsList
 * @return {string[]}
 */
export const cleanEmailList = (emailsList) => {
    const emailRegex = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;
    return emailsList
        .filter((e) => e !== null && e !== undefined)
        .map((e) => e.trim().toLowerCase())
        .filter((email, index, list) => {
            return emailRegex.test(email) && !list.includes(email, index+1);
        });
};

/**
 * Format an email list for displaying it
 * @param {string[]} emailsList
 * @return {string}
 */
export const formatEmailList = (emailsList) => {
    // Bug 1722767: When a mail is wrapped with quotes, we unwrap it
    return emailsList
        .map((mail) => {
            if ( !mail) {
                return '';
            } else if (
                (mail.startsWith('\'') && mail.endsWith('\'')) ||
                (mail.startsWith('"') && mail.endsWith('"')) ||
                (mail.startsWith('<') && mail.endsWith('>')) ||
                (mail.startsWith('(') && mail.endsWith(')')) ||
                (mail.startsWith('{') && mail.endsWith('}')) ||
                (mail.startsWith('[') && mail.endsWith(']'))
            ) {
                return mail.substring(1, mail.length-1);
            } else {
                return mail;
            }
        })
        .filter((email, idx) => email !== '' || (idx+1) === emailsList.length).join('; ');
};

/**
 * Parse an email list from a serialized string
 * @param {string} emailsListString
 * @return {string[]}
 */
export const parseEmailList = (emailsListString) => {
    return emailsListString.split(';').map((e) => e.trim().toLowerCase());
};

/**
 * Merge the newEmails into an existing emails list. Avoid duplicates
 *
 * @param {string[]} emailsList
 * @param {string[]} newEmails
 * @return {string[]}
 */
export const mergeEmailsList = (emailsList, newEmails) => {
    const mergedList = [...emailsList.filter((email) => email)]; // clone the array, as the received array can sometimes be immutable
    newEmails.forEach((newEmail) => {
        if (!mergedList.includes(newEmail)) {
            mergedList.push(newEmail);
        }
    });
    return mergedList;
};

/**
 * Remove a list of email from another one
 * @param {string[]} emailsList
 * @param {string[]} emailsToRemove
 * @return {string[]}
 */
export const deleteEmailsFromList = (emailsList, emailsToRemove) => {
    const editedList = [...emailsList.filter((email) => email)]; // clone the array, as the received array can sometimes be immutable
    emailsToRemove.forEach((toRemove) => {
        const index = editedList.indexOf(toRemove);
        if (index >= 0) {
            editedList.splice(index, 1);
        }
    });

    return editedList;
};

/**
 * Check if an email is valid https://stackoverflow.com/questions/46155/how-can-i-validate-an-email-address-in-javascript
 * @param {string} email
 * @return {RegExpMatchArray}
 */
export const validateEmail = (email) => {
    return String(email)
        .toLowerCase()
        .match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        );
};

/**
 * @param {boolean} multipleReason
 * @param {string[]} reasonsForApproval
 * @param {string} tag
 * @param {string} EPRId
 * @param {object} requester
 * @param {string} vendorCode
 * @param {string} vendorName
 * @param {string} projectTitle
 * @param {string} submissionDate
 * @return {string}
 */
export const getMailContentPRToApprove = (multipleReason, reasonsForApproval, tag, EPRId, requester, vendorCode, vendorName, projectTitle, submissionDate) => {
    const env = Config.getParameter('ENV');

    let linkToPR;
    if (env === 'codev') {
        linkToPR = `https://ezpr-codev.fluidapp.io/?prId=${tag}`;
    } else if (env === 'preprod') {
        linkToPR = `https://easy-request-preprod.fluidapp.io/?prId=${tag}`;
    } else {
        linkToPR = `https://easy-request.fluidapp.io/?prId=${tag}`;
    }

    return ` 
           ${env !== 'prod' ? (`<h3 style='color: ${env === 'codev' ? 'green' : 'red'};
           border-width: 3px;
           border-style: solid;
           ${env === 'codev' ? 'border-color: green' : 'border-color: red'}
           padding: 5px;
           text-align: center'> This notification comes from the ${env === 'codev' ? 'Development' : 'Pre-Production'} system </h3>`) : ''}
            Dear Strategic Buyer,
            <br><br>
            A new Request (Request ID EPR - ${EPRId}) needs your attention and approval.
            <br>
            <table style="width:100%; border: 1px solid #000000">
            <tr>
                <td style="border: 1px solid #000000">ID</td>
                <td style="border: 1px solid #000000">Requester</td>
                <td style="border: 1px solid #000000">Supplier</td>
                <td style="border: 1px solid #000000">Title</td>
                <td style="border: 1px solid #000000">Submission date</td>
            </tr>
            <tr>
                <td style="border: 1px solid #000000">EPR - ${EPRId}</td>
                <td style="border: 1px solid #000000">${requester?.label || ''}</td>
                <td style="border: 1px solid #000000">${vendorCode || ''} - ${vendorName || ''}</td>
                <td style="border: 1px solid #000000">${projectTitle || ''}</td>
                <td style="border: 1px solid #000000">${submissionDate || ''}</td>
            </tr>
            </table>
            <br><br>
            Here ${multipleReason ? 'are' : 'is'} the reason${multipleReason ? 's' : ''} for this validation:
            <br>- 
            ${reasonsForApproval.join('<br>- ')}
            <br><br>
            Click <a href='${linkToPR}' target='_blank'>here</a>, review the request, and take decision accordingly.
            <br><br>
            Thanks and best regards.
            <br>
            Your Procurement team.`;
};


/**
 * @param {string} tag
 * @param {string} EPRId
 * @param {string} vendorCode
 * @param {string} vendorName
 * @param {string} projectTitle
 * @param {string} submissionDate
 * @return {string}
 */
export const getMailContentPRToRequester = (tag, EPRId, vendorCode, vendorName, projectTitle, submissionDate) => {
    const env = Config.getParameter('ENV');

    let linkToPR;
    if (env === 'codev') {
        linkToPR = `https://ezpr-codev.fluidapp.io/?prId=${tag}`;
    } else if (env === 'preprod') {
        linkToPR = `https://easy-request-preprod.fluidapp.io/?prId=${tag}`;
    } else {
        linkToPR = `https://easy-request.fluidapp.io/?prId=${tag}`;
    }

    return ` 
           ${env !== 'prod' ? (`<h3 style='color: ${env === 'codev' ? 'green' : 'red'};
           border-width: 3px;
           border-style: solid;
           ${env === 'codev' ? 'border-color: green' : 'border-color: red'}
           padding: 5px;
           text-align: center'> This notification comes from the ${env === 'codev' ? 'Development' : 'Pre-Production'} system </h3>`) : ''}
            Dear Requesters,
            <br><br>
            A new Request (Request ID EPR - ${EPRId}) needs your attention and action.
            <br>
            <table style="width:100%; border: 1px solid #000000">
            <tr>
                <td style="border: 1px solid #000000">ID</td>
                <td style="border: 1px solid #000000">Supplier</td>
                <td style="border: 1px solid #000000">Title</td>
                <td style="border: 1px solid #000000">Submission date</td>
            </tr>
            <tr>
                <td style="border: 1px solid #000000">EPR - ${EPRId}</td>
                <td style="border: 1px solid #000000">${vendorCode || ''} - ${vendorName || ''}</td>
                <td style="border: 1px solid #000000">${projectTitle || ''}</td>
                <td style="border: 1px solid #000000">${submissionDate || ''}</td>
            </tr>
            </table>
            <br><br>
            Click <a href='${linkToPR}' target='_blank'>here</a> to review the request.
            <br><br>
            Thanks and best regards.
            <br>
            Your Procurement team.`;
};

/**
 * @param {{}} toUser
 * @param {{}} requester
 * @param {{}} spender
 * @param {string} readableDate
 * @param {string} vendorCode
 * @param {string} vendorName
 * @param {string} l1Category
 * @param {string} materialName
 * @param {string} message
 * @param {string} tag
 * @param {string} EPRId
 * @param {string} newStatus
 * @return {string}
 */
export const getMailContentPRRejected = (toUser, requester, spender, readableDate, vendorCode, vendorName, l1Category, materialName, message, tag, EPRId, newStatus) => {
    const env = Config.getParameter('ENV');

    let linkToPR;
    if (env === 'codev') {
        linkToPR = `https://ezpr-codev.fluidapp.io/?prId=${tag}`;
    } else if (env === 'preprod') {
        linkToPR = `https://easy-request-preprod.fluidapp.io/?prId=${tag}`;
    } else {
        linkToPR = `https://easy-request.fluidapp.io/?prId=${tag}`;
    }

    return `
    ${env !== 'prod' ? (`<h3 style='color: ${env === 'codev' ? 'green' : 'red'};
    border-width: 3px;
    border-style: solid;
    ${env === 'codev' ? 'border-color: green' : 'border-color: red'}
    padding: 5px;
    text-align: center'> This notification comes from the ${env === 'codev' ? 'Development' : 'Pre-Production'} system </h3>`) : ''}
    Dear ${toUser.label},
        <br><br>
            Your following request has been <b>rejected:</b>
            <br>
                <table style="width:100%; border: 1px solid #000000">
                    <tr>
                        <td style="border: 1px solid #000000">ID</td>
                        <td style="border: 1px solid #000000">Status</td>
                        <td style="border: 1px solid #000000">Spender</td>
                        <td style="border: 1px solid #000000">Requester</td>
                        <td style="border: 1px solid #000000">Creation Date</td>
                        <td style="border: 1px solid #000000">Supplier</td>
                        <td style="border: 1px solid #000000">Category</td>
                        <td style="border: 1px solid #000000">Material</td>
                    </tr>
                    <tr>
                        <td style="border: 1px solid #000000">EPR-${EPRId}</td>
                        <td style="border: 1px solid #000000">${newStatus}</td>
                        <td style="border: 1px solid #000000">${spender?.label}</td>
                        <td style="border: 1px solid #000000">${requester?.label}</td>
                        <td style="border: 1px solid #000000">${readableDate}</td>
                        <td style="border: 1px solid #000000">${vendorCode} - ${vendorName}</td>
                        <td style="border: 1px solid #000000">${l1Category?.split(' - ')?.[0] || ''}</td>
                        <td style="border: 1px solid #000000">${materialName || ''}</td>
                    </tr>
                </table>
                <br><br>
                    Reason for rejection:
                    <br>
                        ${message}
                        <br><br>
                            Click <a href='${linkToPR}' target='_blank'>here</a>, review the request, and submit it again once changed accordingly or delete it if not required anymore.
                            <br><br>
                                Thanks and best regards.
                                <br>
                                    Your Procurement team.`;
};

/**
 * @param {{}} requester
 * @param {{}} spender
 * @param {string} readableDate
 * @param {string} vendorCode
 * @param {string} vendorName
 * @param {string} l1Category
 * @param {string} materialName
 * @param {string} prNumber
 * @param {boolean} approvedBySB
 * @param {string} tag
 * @param {string} EPRId
 * @return {string}
 */
export const getMailContentPRApproved = (requester, spender, readableDate, vendorCode, vendorName, l1Category, materialName, prNumber, approvedBySB, tag, EPRId) => {
    const env = Config.getParameter('ENV');

    let linkToPR;
    if (env === 'codev') {
        linkToPR = 'https://dev.s2pbrowser.nestle.com/document-flow';
    } else if (env === 'preprod') {
        linkToPR = 'https://test.s2pbrowser.nestle.com/document-flow';
    } else if (env === 'prod') {
        linkToPR = 'https://s2pbrowser.nestle.com/document-flow';
    }

    return `
        ${env !== 'prod' ? (`<h3 style='color: ${env === 'codev' ? 'green' : 'red'};
           border-width: 3px;
           border-style: solid;
           ${env === 'codev' ? 'border-color: green' : 'border-color: red'}
           padding: 5px;
           text-align: center'> This notification comes from the ${env === 'codev' ? 'Development' : 'Pre-Production'} system </h3>`) : ''}
        Dear ${requester?.label},
        <br><br>
        Your following request has been <b>approved</b> by the ${approvedBySB ? 'Strategic Buyer team' : 'Operational Buyer team'}:
        <br>
        <table style="width:100%; border: 1px solid #000000">
            <tr>
                <td style="border: 1px solid #000000">ID</td>
                <td style="border: 1px solid #000000">Status</td>
                <td style="border: 1px solid #000000">Spender</td>
                <td style="border: 1px solid #000000">Requester</td>
                <td style="border: 1px solid #000000">Creation Date</td>
                <td style="border: 1px solid #000000">Supplier</td>
                <td style="border: 1px solid #000000">Category</td>
                <td style="border: 1px solid #000000">Material</td>
            </tr>
            <tr>
                <td style="border: 1px solid #000000">EPR - ${EPRId}</td>
                <td style="border: 1px solid #000000">Approved</td>
                <td style="border: 1px solid #000000">${spender?.label}</td>
                <td style="border: 1px solid #000000">${requester?.label}</td>
                <td style="border: 1px solid #000000">${readableDate}</td>
                <td style="border: 1px solid #000000">${vendorCode} - ${vendorName}</td>
                <td style="border: 1px solid #000000">${l1Category?.split(' - ')?.[0] || ''}</td>
                <td style="border: 1px solid #000000">${materialName || ''}</td>
            </tr>
        </table>
        <br><br>
        You can now monitor its status and make modifications if needed via the <a href='${linkToPR}' target='_blank'>Request cockpit</a> under the SAP Purchase Requisition number ${prNumber}.
        <br><br>
        Thanks and best regards.
        <br>
        Your Procurement team.`;
};

/**
 * @param {{}} requester
 * @param {{}} spender
 * @param {string} readableDate
 * @param {string} vendorCode
 * @param {string} vendorName
 * @param {string} l1Category
 * @param {string} materialName
 * @param {string} prNumber
 * @param {string} tag
 * @param {string} EPRId
 * @return {string}
 */
export const getMailContentPRSubmitted = (requester, spender, readableDate, vendorCode, vendorName, l1Category, materialName, prNumber, tag, EPRId) => {
    const env = Config.getParameter('ENV');

    let linkToPR;
    if (env === 'codev') {
        linkToPR = 'https://dev.s2pbrowser.nestle.com/document-flow';
    } else if (env === 'preprod') {
        linkToPR = 'https://test.s2pbrowser.nestle.com/document-flow';
    } else if (env === 'prod') {
        linkToPR = 'https://s2pbrowser.nestle.com/document-flow';
    }

    return `
        ${env !== 'prod' ? (`<h3 style='color: ${env === 'codev' ? 'green' : 'red'};
           border-width: 3px;
           border-style: solid;
           ${env === 'codev' ? 'border-color: green' : 'border-color: red'}
           padding: 5px;
           text-align: center'> This notification comes from the ${env === 'codev' ? 'Development' : 'Pre-Production'} system </h3>`) : ''}
        Dear ${requester?.label},
        <br><br>
        Your following request has been <b>Submitted</b>:
        <br>
        <table style="width:100%; border: 1px solid #000000">
            <tr>
                <td style="border: 1px solid #000000">ID</td>
                <td style="border: 1px solid #000000">Status</td>
                <td style="border: 1px solid #000000">Spender</td>
                <td style="border: 1px solid #000000">Requester</td>
                <td style="border: 1px solid #000000">Creation Date</td>
                <td style="border: 1px solid #000000">Supplier</td>
                <td style="border: 1px solid #000000">Category</td>
                <td style="border: 1px solid #000000">Material</td>
            </tr>
            <tr>
                <td style="border: 1px solid #000000">EPR - ${EPRId}</td>
                <td style="border: 1px solid #000000">Submitted</td>
                <td style="border: 1px solid #000000">${spender?.label}</td>
                <td style="border: 1px solid #000000">${requester?.label}</td>
                <td style="border: 1px solid #000000">${readableDate}</td>
                <td style="border: 1px solid #000000">${vendorCode} - ${vendorName}</td>
                <td style="border: 1px solid #000000">${l1Category?.split(' - ')?.[0] || ''}</td>
                <td style="border: 1px solid #000000">${materialName || ''}</td>
            </tr>
        </table>
        <br><br>
        You can now monitor its status and make modifications if needed via the <a href='${linkToPR}' target='_blank'>Request cockpit</a> under the SAP Purchase Requisition number ${prNumber}.
        <br><br>
        Thanks and best regards.
        <br>
        Your Procurement team.`;
};

export const getMailContentPRMassSubmitted = (requester, prs) => {
    const env = Config.getParameter('ENV');

    let linkToPR;
    if (env === 'codev') {
        linkToPR = 'https://dev.s2pbrowser.nestle.com/document-flow';
    } else if (env === 'preprod') {
        linkToPR = 'https://test.s2pbrowser.nestle.com/document-flow';
    } else if (env === 'prod') {
        linkToPR = 'https://s2pbrowser.nestle.com/document-flow';
    }

    const readableDateOptions = {month: 'short', day: 'numeric', year: 'numeric'};

    const prRows = prs.map((pr) => {
        const prJson = pr.prJson;
        const readableDate = new Date(prJson.creationDate).toLocaleString('en-US', readableDateOptions);
        const materialName = Array.isArray(prJson.materials) && prJson.materials.length > 0 ? prJson.materials[0].name : '';

        return `
            <tr>
                <td style="border: 1px solid #000000">EPR - ${prJson.EPRId || tagToDisplayER(prJson.tag)}</td>
                <td style="border: 1px solid #000000">${pr.SAPPRNumber}</td>
                <td style="border: 1px solid #000000">Submitted</td>
                <td style="border: 1px solid #000000">${prJson.spender?.label}</td>
                <td style="border: 1px solid #000000">${prJson.requester?.label}</td>
                <td style="border: 1px solid #000000">${readableDate}</td>
                <td style="border: 1px solid #000000">${prJson.vendorCode} - ${prJson.vendorName}</td>
                <td style="border: 1px solid #000000">${prJson.l1Category?.split(' - ')?.[0] || ''}</td>
                <td style="border: 1px solid #000000">${materialName}</td>
            </tr>`;
    }).join('');

    return `
        ${env !== 'prod' ? (`<h3 style='color: ${env === 'codev' ? 'green' : 'red'};
           border-width: 3px;
           border-style: solid;
           ${env === 'codev' ? 'border-color: green' : 'border-color: red'}
           padding: 5px;
           text-align: center'> This notification comes from the ${env === 'codev' ? 'Development' : 'Pre-Production'} system </h3>`) : ''}
        Dear ${requester?.label},
        <br><br>
        Your following request(s) have been <b>Submitted</b>:
        <br>
        <table style="width:100%; border: 1px solid #000000">
            <tr>
                <td style="border: 1px solid #000000">ID</td>
                <td style="border: 1px solid #000000">SAP PR Number</td>
                <td style="border: 1px solid #000000">Status</td>
                <td style="border: 1px solid #000000">Spender</td>
                <td style="border: 1px solid #000000">Requester</td>
                <td style="border: 1px solid #000000">Creation Date</td>
                <td style="border: 1px solid #000000">Supplier</td>
                <td style="border: 1px solid #000000">Category</td>
                <td style="border: 1px solid #000000">Material</td>
            </tr>
            ${prRows}
        </table>
        <br><br>
        You can now monitor its status and make modifications if needed via the <a href='${linkToPR}' target='_blank'>Request cockpit</a>.
        <br><br>
        Thanks and best regards.
        <br>
        Your Procurement team.`;
};


export const getMailContentAdminAppChange = (companyCode, changes) => {
    const env = Config.getParameter('ENV');
    const changeRows = changes.map((change) => `
        <tr>
            <td style="border: 1px solid #000000">${change.idx || ''}</td>
            <td style="border: 1px solid #000000">${companyCode || ''}</td>
            <td style="border: 1px solid #000000">${change.date || ''}</td>
            <td style="border: 1px solid #000000">${change.userName || ''}</td>
            <td style="border: 1px solid #000000">${change.userID || ''}</td>
            <td style="border: 1px solid #000000">${change.table || ''}</td>
            <td style="border: 1px solid #000000">${change.column || ''}</td>
            <td style="border: 1px solid #000000">${change.oldValue || ''}</td>
            <td style="border: 1px solid #000000">${change.newValue || ''}</td>
        </tr>
    `).join('');

    return `
        ${env !== 'prod' ? (`<h3 style='color: ${env === 'codev' ? 'green' : 'red'};
           border-width: 3px;
           border-style: solid;
           ${env === 'codev' ? 'border-color: green' : 'border-color: red'}
           padding: 5px;
           text-align: center'> This notification comes from the ${env === 'codev' ? 'Development' : 'Pre-Production'} system </h3>`) : ''}
        Hello,  
        <br>
        The following changes have been made for company code: <b>${companyCode || ''}</b>:
        <br><br>
        <table style="width:100%; border: 1px solid #000000">
            <tr>
                <td style="border: 1px solid #000000">ID</td>
                <td style="border: 1px solid #000000">Company Code</td>
                <td style="border: 1px solid #000000">Date</td>
                <td style="border: 1px solid #000000">User name</td>
                <td style="border: 1px solid #000000">User ID</td>
                <td style="border: 1px solid #000000">Table</td>
                <td style="border: 1px solid #000000">Column</td>
                <td style="border: 1px solid #000000">Old value</td>
                <td style="border: 1px solid #000000">New value</td>
            </tr>
            ${changeRows}
        </table>
        <br><br>
        Thanks and best regards.
        <br>
        Your Procurement team.`;
};


/**
 * /**
 * @param {[]} prList
 * @param {[]} invalidPrList
 * @param {[]} notCreatedPrList
 * @return {string}
 */
export const getMailContentMassCreationPRS = (prList, invalidPrList, notCreatedPrList) => {
    const env = Config.getParameter('ENV');
    const invalidRows = invalidPrList?.map((invalid) => `
    <tr>
        <td style="border: 1px solid #000000">${invalid.id || ''}</td>
        <td style="border: 1px solid #000000; background-color: orange; color: white;">WARNING</td>
        <td style="border: 1px solid #000000">Validation failed for the following Material PSL Id: ${invalid.materialPSLId || ''}</td>
    </tr>
`).join('') || '';
    const notCreatedPrListRows = notCreatedPrList?.map((notCreated) => `
    <tr>
        <td style="border: 1px solid #000000">PR not created for EPR-ID ${notCreated.id || ''}</td>
        <td style="border: 1px solid #000000; background-color: red; color: white;">ERROR</td>
        <td style="border: 1px solid #000000">${notCreated.errorPrs?.[0]?.msg || 'Something went wrong.'}</td>
    </tr>
`).join('') || '';
    const successRows = prList?.map((successItem) => `
    <tr>
        <td style="border: 1px solid #000000">EPR-${successItem.eprId || ''}</td>
        <td style="border: 1px solid #000000; background-color: ${successItem.type === 'WARNING' ? 'orange' : 'green'}; color: white;">${successItem.type}</td>
        <td style="border: 1px solid #000000">${successItem.msg || ''}</td>
    </tr>
`,
    ).join('') || '';
    return `
        ${env !== 'prod' ? (`<h3 style='color: ${env === 'codev' ? 'green' : 'red'};
           border-width: 3px;
           border-style: solid;
           ${env === 'codev' ? 'border-color: green' : 'border-color: red'}
           padding: 5px;
           text-align: center'> This notification comes from the ${env === 'codev' ? 'Development' : 'Pre-Production'} system </h3>`) : ''}
        Dear user,
        <br><br>
        Your requests have been <b>created</b>.
        <br>
        <br>
        You can see the newly created EPR's in the attached file .
        <br><br>
        Bellow you can see the status for all the EPR's that where processed:
        <br><br>
        <table style="width:100%; border: 1px solid #000000">
            <tr>
                <td style="border: 1px solid #000000">EPR Id</td>
                <td style="border: 1px solid #000000">Status</td>
                <td style="border: 1px solid #000000">Message</td>
            </tr>
            ${invalidRows}
            ${notCreatedPrListRows}
            ${successRows}
        </table>
        <br><br>
        Thanks and best regards.
        <br>
        Your Procurement team.`;
};
